import React, { useState, useEffect } from 'react';
import SideMenu from "../../component/SideMenu";
import Topbar from "../../component/Topbar";
import SettingNav from './SettingNav';
import { LuBell, LuSmartphone } from "react-icons/lu";
import "../../asset/css/profile-info.css";
import { baseurl } from '../../baseurl';
import axios from 'axios';
import Swal from 'sweetalert2';

// Reuse the skeleton from the Measurement page
const SkeletonCard = () => (
    <div className="premium-card h-100 skeleton-animate">
        <div className="section-header">
            <div className="skeleton-circle"></div>
            <div style={{ flex: 1 }}>
                <div className="skeleton-line short"></div>
                <div className="skeleton-line"></div>
            </div>
        </div>
        <div className="form-body mt-3">
            {[1, 2, 3, 4].map(i => (
                <div key={i} className="d-flex mb-3">
                    <div className="skeleton-line" style={{ width: '40px', height: '20px', borderRadius: '20px', marginRight: '15px' }}></div>
                    <div style={{ flex: 1 }}><div className="skeleton-line"></div></div>
                </div>
            ))}
        </div>
    </div>
);

const Notification = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    
    // Default state including both email and in-app groups
    const [config, setConfig] = useState({
        emailSettings: {
            projectUpdates: true,
            reportReady: true,
            teamActivity: true,
            marketingEmails: false,
        },
        inAppSettings: {
            newProjectAlert: true,
            reportReadyAlert: true,
            teamMentions: true,
            systemUpdates: true,
        }
    });

    const userToken = localStorage.getItem('userToken');
    const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
    });

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await axios.get(`${baseurl}getUserPreferences`, {
                    headers: { Authorization: `Bearer ${userToken}` }
                });
                if (res.data.success && res.data.data.notification_settings) {
                    // Merging to ensure UI doesn't break if new keys are added later
                    setConfig(prev => ({
                        ...prev,
                        ...res.data.data.notification_settings
                    }));
                }
            } catch (err) {
                console.error("Fetch Error:", err);
            } finally {
                setTimeout(() => setFetching(false), 500);
            }
        };
        fetchSettings();
    }, [userToken]);

    const toggleSetting = (group, key) => {
        setConfig(prev => ({
            ...prev,
            [group]: {
                ...prev[group],
                [key]: !prev[group][key]
            }
        }));
    };

    const saveSettings = async () => {
        setIsLoading(true);
        try {
            const res = await axios.put(`${baseurl}updateUserPreferences`, {
                column: 'notification_settings',
                data: config // This sends the entire nested object
            }, {
                headers: { Authorization: `Bearer ${userToken}` }
            });

            if (res.data.success) {
                Toast.fire({ icon: 'success', title: 'Notifications updated' });
            }
        } catch (err) {
            Toast.fire({ icon: 'error', title: 'Update failed' });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="app-container">
            <Topbar />
            <div className="main-wrapper" style={{ background: '#F4F7FE' }}>
                <SideMenu />
                <div className="profile-content-container p-3" style={{ flex: 1, overflow: 'auto' }}>
                    <SettingNav />

                    <div className="row g-4 mt-0">
                        {fetching ? (
                            <>
                                <div className="col-lg-6"><SkeletonCard /></div>
                                <div className="col-lg-6"><SkeletonCard /></div>
                            </>
                        ) : (
                            <>
                                {/* LEFT CARD: Email Notifications */}
                                <div className="col-lg-6">
                                    <div className="premium-card h-100">
                                        <div className="section-header">
                                            <div className="icon-wrap security-icon" style={{ background: '#4318FF' }}>
                                                <LuBell style={{ color: '#fff' }} />
                                            </div>
                                            <div>
                                                <h5 className="card-title">Email Notifications</h5>
                                                <p className="card-subtitle">Choose what emails you receive</p>
                                            </div>
                                        </div>

                                        <div className="notification-box-list">
                                            <NotificationToggle 
                                                title="Project Updates" 
                                                desc="When projects are completed or updated" 
                                                active={config.emailSettings.projectUpdates}
                                                onToggle={() => toggleSetting('emailSettings', 'projectUpdates')}
                                            />
                                            <NotificationToggle 
                                                title="Report Ready" 
                                                desc="When measurement reports are generated" 
                                                active={config.emailSettings.reportReady}
                                                onToggle={() => toggleSetting('emailSettings', 'reportReady')}
                                            />
                                            <NotificationToggle 
                                                title="Team Activity" 
                                                desc="When team members add projects" 
                                                active={config.emailSettings.teamActivity}
                                                onToggle={() => toggleSetting('emailSettings', 'teamActivity')}
                                            />
                                            <NotificationToggle 
                                                title="Marketing Emails" 
                                                desc="Tips, new features, and promotions" 
                                                active={config.emailSettings.marketingEmails}
                                                onToggle={() => toggleSetting('emailSettings', 'marketingEmails')}
                                            />
                                        </div>

                                        <div className="card-footer-actions mt-auto pt-4">
                                            <button className="btn-save-changes w-100" onClick={saveSettings} disabled={isLoading}>
                                                {isLoading ? 'Saving...' : 'Save Email Settings'}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* RIGHT CARD: In-App Notifications */}
                                <div className="col-lg-6">
                                    <div className="premium-card h-100">
                                        <div className="section-header">
                                            <div className="icon-wrap security-icon" style={{ background: '#4318FF' }}>
                                                <LuSmartphone style={{ color: '#fff' }} />
                                            </div>
                                            <div>
                                                <h5 className="card-title">In-App Notifications</h5>
                                                <p className="card-subtitle">Control dashboard notifications</p>
                                            </div>
                                        </div>

                                        <div className="notification-box-list">
                                            <NotificationToggle 
                                                title="New Project Alert" 
                                                desc="When new projects are created" 
                                                active={config.inAppSettings.newProjectAlert}
                                                onToggle={() => toggleSetting('inAppSettings', 'newProjectAlert')}
                                            />
                                            <NotificationToggle 
                                                title="Report Ready Alert" 
                                                desc="When reports are generated" 
                                                active={config.inAppSettings.reportReadyAlert}
                                                onToggle={() => toggleSetting('inAppSettings', 'reportReadyAlert')}
                                            />
                                            <NotificationToggle 
                                                title="Team Mentions" 
                                                desc="When team members mention you" 
                                                active={config.inAppSettings.teamMentions}
                                                onToggle={() => toggleSetting('inAppSettings', 'teamMentions')}
                                            />
                                            <NotificationToggle 
                                                title="System Updates" 
                                                desc="About new features and maintenance" 
                                                active={config.inAppSettings.systemUpdates}
                                                onToggle={() => toggleSetting('inAppSettings', 'systemUpdates')}
                                            />
                                        </div>

                                        <div className="card-footer-actions mt-auto pt-4">
                                            <button className="btn-save-changes w-100" onClick={saveSettings} disabled={isLoading}>
                                                {isLoading ? 'Saving...' : 'Save In-App Settings'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const NotificationToggle = ({ title, desc, active, onToggle }) => (
    <div className="notification-toggle-item d-flex align-items-center mb-3">
        <label className="custom-switch me-3">
            <input type="checkbox" checked={active} onChange={onToggle} />
            <span className="custom-slider"></span>
        </label>
        <div className="toggle-text">
            <div className="toggle-title  " style={{ fontSize: '13px', fontWeight: '500' }}>{title}</div>
            <div className="toggle-desc text-muted" style={{ fontSize: '11px' }}>{desc}</div>
        </div>
    </div>
);

export default Notification;